import { useEffect } from 'react';
import useShowEvents from './common/hooks/useShowEvents';
import ShowPageDesktop from './desktop';
import ShowPageMobile from './mobile';
import getServerSidePropsFunction from './common/getServerSideProps';
import { ShowPageProvider } from './common/contexts/page';
import useEpisodes from './common/hooks/useEpisodes';
import useShowUrlParams from '@/hooks/shows/useShowUrlParams';
import ShowMeta from '@/components/app/ShowMeta';
import useFBPixel from '@/hooks/fb/useFBPixel';
import Page from '@/components/app/Page';
import useShow from '@/hooks/shows/useShowFromURL';
import useShouldRenderForDesktop from '@/hooks/layout/useShouldRenderForDesktop';
import useShouldRenderForMobile from '@/hooks/layout/useShouldRenderForMobile';
import {
  isValidNumberParam, isValidSlugParam, isValidNumberOrZeroParam
} from '@/helpers/routeParamChecks';

const Show = () => {
  const shouldRenderForMobile = useShouldRenderForMobile();
  const shouldRenderForDesktop = useShouldRenderForDesktop();

  const {
    showSlug: slugInRoute,
    episodeNumber: episodeNumberInRoute,
    seasonNumber: seasonNumberInRoute,
    isEpisodePage
  } = useShowUrlParams();

  const {
    isError,
    notFound,
    show,
    refetch: refetchShow
  } = useShow();

  useEpisodes();

  // Check route for valid parameters. It should contain a valid slug parameter.
  // If the page is an episode page, it should also contain a valid episode number and season number.
  const paramsAreValid = isValidSlugParam(slugInRoute) && (
    isEpisodePage
      ? (
        isValidNumberOrZeroParam(episodeNumberInRoute)
        && isValidNumberParam(seasonNumberInRoute)
      )
      : true
  );

  // Extract show details
  const {
    id: showId,
    title: showTitle,
    facebook_pixel_id
  } = show;

  // Send page events
  useShowEvents({
    showId,
    showTitle
  });

  const { sendFBPixelPageView } = useFBPixel();

  useEffect(() => {
    // Send a page view event to the show's pixel id
    if (facebook_pixel_id && paramsAreValid) {
      sendFBPixelPageView({ pixelId: facebook_pixel_id });
    }
  }, [facebook_pixel_id, paramsAreValid, sendFBPixelPageView]);

  // Key used to force rerender if we change channel/episode
  const pageKey = `${ slugInRoute }-${ episodeNumberInRoute }`;

  return (
    <Page
      error={ isError }
      key={ pageKey }
      meta={ (
        <ShowMeta
          episodeNumber={ episodeNumberInRoute }
          notFound={ !paramsAreValid || notFound }
          seasonNumber={ seasonNumberInRoute }
          showId={ showId }
          showSlug={ slugInRoute }
        />
      ) }
      notFound={ !paramsAreValid || notFound }
      onClickRetry={ refetchShow }
      pageId={ pageKey }
    >
      <ShowPageProvider>
        { shouldRenderForDesktop && <ShowPageDesktop /> }
        { shouldRenderForMobile && <ShowPageMobile /> }
      </ShowPageProvider>
    </Page>
  );
};

export const getServerSideProps = getServerSidePropsFunction;

export default Show;